import React, { useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { ReactSelectField } from "../../../../../../atoms/FormElement";
import { toSequenceNumberOrNull, validateBreakdownSequenceValue } from "./sequenceValidation";
import type { BreakdownFormValues } from "./types";

type SequenceField = "assignedInstallmentNo" | "assignedTorPeriodNo";

type BreakdownSequenceCellProps = {
  idx: number;
  field: SequenceField;
  maxCount: number;
  disabled?: boolean;
};

const cellStyle: React.CSSProperties = { marginBottom: 0 };

export const BreakdownSequenceCell = (props: BreakdownSequenceCellProps) => {
  const { idx, field, maxCount, disabled = false } = props;

  const {
    control,
    getValues,
    formState: { errors },
  } = useFormContext<BreakdownFormValues>();

  const watchedRows = useWatch({ control, name: "rows" });

  const options = useMemo(
    () => Array.from({ length: maxCount }, (_, i) => ({ label: i + 1, value: i + 1 })),
    [maxCount],
  );

  const getPreviousSelectedValue = (): number | null => {
    for (let i = idx - 1; i >= 0; i--) {
      const val = toSequenceNumberOrNull(watchedRows?.[i]?.[field]?.value);
      if (val != null) return val;
    }
    return null;
  };

  const getAllowedValues = (): number[] => {
    const previousValue = getPreviousSelectedValue();
    if (previousValue == null) return [1];
    if (previousValue >= maxCount) return [previousValue];
    return [previousValue, previousValue + 1];
  };

  const allowedValues = getAllowedValues();
  const selectableOptions = options.map((option) => ({
    ...option,
    isDisabled: !allowedValues.includes(option.value),
  }));

  const validate = (value: { value: number | string } | null) => {
    const allRows = getValues("rows");
    return validateBreakdownSequenceValue({
      value,
      previousValues: allRows.slice(0, idx).map((row) => row?.[field]?.value),
      maxCount,
    });
  };

  return (
    <ReactSelectField
      fieldname={`rows.${idx}.${field}`}
      control={control}
      selectOption={selectableOptions}
      style={cellStyle}
      classes="mb-0"
      iProps={{
        menuPlacement: "auto",
        isOptionDisabled: (option) => Boolean(option?.isDisabled),
        isDisabled: disabled,
      }}
      rules={disabled ? {} : { required: "Required", validate }}
      validate={!disabled && errors?.rows?.[idx]?.[field] && "error"}
      validMessage={!disabled ? errors?.rows?.[idx]?.[field]?.message : undefined}
    />
  );
};
